import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";

import * as cartRequests from "@/services/api/cart";
import {
  IOrderItem,
  ICartParams,
  IOrderParams,
  ICartPayload,
} from "@/services/interfaces/requests";
import { IError, IPageMeta, IProduct } from "@/services/interfaces/";
import { declension } from "@/services/utils";

import { ProductProperties } from "./enums";

import { Catalog } from "@/store/catalog/enums";
import { IChangePropArgs, IQuantity, ISort } from "./interfaces";
import { calculateTotal } from "./utils";
import { ICartData } from "./types";

const baseMeta = {
  h1: "Корзина",
  breadcrumbs: [{ url: "/", name: "Главная" }, { name: "Корзина" }],
  title: "МТК - Корзина",
};

@Module({
  name: "cart",
  namespaced: true,
})
export default class Cart extends VuexModule {
  products: IProduct[] = [];
  meta: IPageMeta = baseMeta;
  sort: ISort = {
    name: "",
    direction: null,
  };
  cart: ICartData = {
    amount: "0",
    total_quantity: 0,
    products_count: 0,
  };
  error: IError | null = null;
  order: IOrderItem[] = [];

  get getProducts() {
    return this.products;
  }
  get getProductIds() {
    return this.products.map((product) => product.id);
  }
  get getOrder() {
    return this.order;
  }
  get getError() {
    return this.error;
  }
  get getMeta() {
    return this.meta;
  }
  get getSort() {
    return this.sort;
  }

  get getQuantity() {
    const quantity: IQuantity = {};
    this.products.forEach((product: IProduct) => {
      quantity[product.id] = product.quantity;
    });
    return quantity;
  }

  get getCartQuantity(): number {
    return this.cart.total_quantity;
  }

  get getCartSum(): number {
    return parseInt(this.cart.amount, 10);
  }

  get getPurchaseTotalSum(): number {
    return this.order.reduce(
      (acc: number, order: IOrderItem) =>
        acc + (Number(order.product?.custom_total_price) || 0),
      0,
    );
  }

  get getBenefitTotalSum(): number {
    return this.order.reduce(
      (acc: number, order: IOrderItem) =>
        acc + (Number(order.product?.benefit) || 0),
      0,
    );
  }

  get getOrderQuantity(): number {
    return this.order.reduce(
      (acc: number, product: IOrderItem) => acc + (product.quantity || 0),
      0,
    );
  }

  get getOrderSum(): number {
    return this.order.reduce(
      (acc: number, product: IOrderItem) =>
        acc + (product.quantity || 0) * ((product.price as number) || 0),
      0,
    );
  }

  @Mutation
  ADD_PRODUCT(data: any) {
    this.products.push(data);
  }

  @Mutation
  CHANGE_PRODUCT_PROPERTY(data: IChangePropArgs) {
    const index = this.products.findIndex(
      (prod: IProduct) => prod.id === data.id,
    );
    const product = this.products[index];
    product[data.propertyName] = data.value as any;
    this.products[index] = product;
  }

  @Mutation
  REMOVE_PRODUCT(data: IProduct) {
    const index = this.products.findIndex((prod) => prod.id === data.id);
    if (index >= -1) {
      this.products = this.products.splice(index, 1);
    }
  }

  @Mutation
  SET_PRODUCTS(data: IProduct[]) {
    this.products = data;
  }

  @Mutation
  SET_PAGE_ERROR(data: IError) {
    this.error = data;
  }

  @Mutation
  SET_META(data: IPageMeta) {
    this.meta = data;
  }

  @Mutation
  ORDER_SET(data: Array<IOrderItem>) {
    this.order = data;
  }

  @Mutation
  ORDER_CLEAR() {
    this.order = [];
  }

  @Mutation
  SET_CART_DATA(data: ICartData) {
    this.cart = data;
  }

  @Mutation
  SORT_SET(data: ISort) {
    this.sort = data;
  }

  @Action
  async addProduct(params: ICartPayload): Promise<void> {
    const res = await cartRequests.add(params);
    if (res.status === 201) {
      this.getCart();
    }
  }

  @Action
  async removeProduct(params: ICartParams): Promise<void> {
    const res = await cartRequests.remove(params);
    if (res.status === 204) {
      this.getCart();
    }
  }

  @Action
  async removeAllProducts(): Promise<void> {
    const res = await cartRequests.clear();
    if (res.status === 204) {
      this.getCart();
    }
  }

  @Action
  async changeQuantity(params: ICartParams): Promise<void> {
    const res = await cartRequests.change(params);
    if (res.status === 201) {
      this.getCart();
      // this.CHANGE_PRODUCT_PROPERTY({
      //   id: params.id,
      //   value: res.data.quantity,
      //   propertyName: ProductProperties.QUANTITY,
      // });
    }
  }

  @Action
  async createOrder(params: IOrderParams): Promise<any> {
    const res = await cartRequests.order(params);
    if (res.status === 201) {
      if (params.data.isFromCart) {
        const productsOrderIds = params.data.products.map((item) => item.id);
        const productsCart = this.getProducts.filter(
          (item) => !productsOrderIds.includes(item.id),
        );
        this.SET_PRODUCTS(productsCart);
        this.SET_META(baseMeta);
        this.getCart();
      } else {
        const product = params.data.products[0];
        if (product) {
          this.context.commit(
            "catalog/" + Catalog.CHANGE_QUANTITY,
            { id: product.id, val: 1 },
            { root: true },
          );
        }
      }
    }
    return res;
  }

  @Action({ rawError: true })
  async getCart(data: any = {}): Promise<void> {
    if (this.sort.name) {
      data.params = { sort: this.sort.name, order: this.sort.direction };
    }
    const res = await cartRequests.get(data);
    if (res.status === 200) {
      const { amount, total_quantity, products, products_count } =
        res.data.data;

      // if (products.length)
      //   products[0] = {
      //     ...products[0],
      //     price: 1400,
      //     promo_price: 1200,
      //     promo_stock: 4,
      //     stock: 20,
      //     promo_end: "2024-12-01 06:29:00",
      //     promo_start: "2023-04-10 06:29:00",
      //     quantity: 10,
      //   };

      this.SET_CART_DATA({ amount, total_quantity, products_count });
      this.SET_PRODUCTS(products);

      const meta = Object.assign({}, this.meta);
      meta.title = amount
        ? `${baseMeta.title} | ${amount} ${declension(amount, [
            "товар",
            "товара",
            "товаров",
          ])}`
        : baseMeta.title;

      this.SET_META(meta);
    }
  }
}
